import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { justForSideEffectWithPayload } from 'src/infrastructure/saga.helpers'
import { cloneDeep } from 'lodash'
import {
  BadgeStatus, ApplyInvoicesManagementSagaParams, GetPendingReceivementPayload, GetReceivementConferenceParams, PendingReceivementsState, ReceivementConference, SendReceivementConferenceParams, SetPendingReceivements, ValidateInvoiceKeySagaParams,
  AssociateProductsReceivementPayload,
  ConfirmWrongProductPayload,
} from './PendingReceivementsTypes'

export const initialState: PendingReceivementsState = {
  pendingReceivements: [],
  loading: false,
  badgeStatus: BadgeStatus.PENDING,
  sendReceivementConferenceLoading: false,
  approveCheckboxesValues: {},
}

const getPendingReceivements = justForSideEffectWithPayload<GetPendingReceivementPayload>()
const getReceivementConference = justForSideEffectWithPayload<GetReceivementConferenceParams>()
const sendReceivementConference = justForSideEffectWithPayload<SendReceivementConferenceParams>()
const validateSupervisorBadge = justForSideEffectWithPayload<string>()
const validateInvoiceKey = justForSideEffectWithPayload<ValidateInvoiceKeySagaParams>()
const finishInvoicesManagement = justForSideEffectWithPayload<ApplyInvoicesManagementSagaParams>()
const associateProductsReceivementThirdConference = justForSideEffectWithPayload<AssociateProductsReceivementPayload>()
const confirmWrongProduct = justForSideEffectWithPayload<ConfirmWrongProductPayload>()

const setPendingReceivements = (
  state: PendingReceivementsState,
  action: PayloadAction<SetPendingReceivements>,
): PendingReceivementsState => ({
  ...state,
  pendingReceivements: action.payload.pendingReceivements,
})

const setLoading = (
  state: PendingReceivementsState,
  { payload }: PayloadAction<boolean>,
): PendingReceivementsState => ({
  ...state,
  loading: payload,
})

const setReceivementConferenceLoading = (
  state: PendingReceivementsState,
  { payload }: PayloadAction<boolean>,
): PendingReceivementsState => ({
  ...state,
  sendReceivementConferenceLoading: payload,
})

const setBadge = (
  state: PendingReceivementsState,
  { payload }: PayloadAction<string>,
): PendingReceivementsState => ({
  ...state,
  badge: payload,
})

const setBadgeStatus = (
  state: PendingReceivementsState,
  { payload }: PayloadAction<BadgeStatus>,
): PendingReceivementsState => ({
  ...state,
  badgeStatus: payload,
})

const setReceivementConference = (
  state: PendingReceivementsState,
  { payload }: PayloadAction<ReceivementConference>,
): PendingReceivementsState => ({
  ...state,
  receivementConference: payload,
})

const setApproveCheckboxesValues = (
  state: PendingReceivementsState,
  action: PayloadAction<Record<number, boolean>>,
): PendingReceivementsState => ({
  ...state,
  approveCheckboxesValues: action.payload,
})

const setConfirmWrongProduct = (state: PendingReceivementsState, action: PayloadAction<number>): PendingReceivementsState => {
  const idProductDivergence = action.payload
  const cloneProductWrongState = cloneDeep(state.receivementConference?.productWrong)

  if (cloneProductWrongState === undefined) return state

  const currentItemIndex = cloneProductWrongState?.findIndex((item) => item.idProductDivergence === idProductDivergence)

  if (currentItemIndex === -1) return state

  cloneProductWrongState[currentItemIndex].wrongProductConfirmed = true

  return {
    ...state,
    receivementConference: {
      ...state.receivementConference as ReceivementConference,
      productWrong: cloneProductWrongState,
    },
  }
}

const slice = createSlice({
  name: 'PendingReceivements',
  initialState,
  reducers: {
    getPendingReceivements,
    setPendingReceivements,
    setLoading,
    setReceivementConferenceLoading,
    setReceivementConference,
    getReceivementConference,
    setBadge,
    setBadgeStatus,
    validateSupervisorBadge,
    sendReceivementConference,
    setApproveCheckboxesValues,
    validateInvoiceKey,
    applyInvoicesManagement: finishInvoicesManagement,
    associateProductsReceivementThirdConference,
    confirmWrongProduct,
    setConfirmWrongProduct,
  },
})

export default slice.reducer

export const { actions } = slice
